import { useInfiniteQuery } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";

import type { S3LevelPage, S3ObjectEntry } from "../S3ObjectEntry";

import { integrationQueryKeys } from "./integrationQueryKeys";

export interface UseS3BucketEntriesParams {
  bucketName: string;
  orgId: string;
  prefix: string;
}

export interface UseS3BucketEntriesResult {
  /** The current page's folders and objects, as S3 returned them. */
  data: S3ObjectEntry[] | undefined;
  error: Error | null;
  isLoading: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
}

const PAGE_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * One page of a prefix's immediate children: its folders and its objects together.
 *
 * S3 pages both as a single key-ordered stream and counts a rolled-up folder against the same
 * page size as an object, so a page holds however many of each that stretch of keys contained.
 * This is what the S3 console shows, and a folder can therefore land on a later page than the
 * objects beside it.
 *
 * Follows the `useListOrgs` shape — an `useInfiniteQuery` chained by `next_token`, exposing the
 * `currentPage` slice — with one deliberate difference: it does not prefetch. Every page is a
 * live call against a customer's bucket on an admin's behalf, so a page is fetched when the
 * operator asks for it.
 */
export function useS3BucketEntries(
  params: UseS3BucketEntriesParams,
  currentPage: number,
  pageSize: number,
): UseS3BucketEntriesResult {
  const { integrationService } = useDomainServices();
  const { bucketName, orgId, prefix } = params;

  const q = useInfiniteQuery({
    queryKey: integrationQueryKeys.s3Listing(
      bucketName,
      orgId,
      prefix,
      "objects",
      pageSize,
    ),
    queryFn: ({ pageParam, signal }) =>
      integrationService.listS3BucketObjects(
        {
          bucketName,
          orgId,
          prefix,
          kind: "objects",
          pageToken: pageParam,
          limit: pageSize,
        },
        { abortSignal: signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: S3LevelPage) => last.next_token ?? undefined,
    // No `keepPreviousData`: holding the previous prefix's rows while the table paints skeletons
    // over them leaves the page exporting one folder's contents under another folder's name.
    enabled: bucketName.length > 0,
    staleTime: PAGE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });

  const {
    data,
    hasNextPage: hasUnfetchedPage,
    isFetchingNextPage,
    fetchNextPage,
  } = q;
  React.useEffect(
    function fetchPageWhenAsked() {
      if (!data || isFetchingNextPage || !hasUnfetchedPage) {
        return;
      }
      if (currentPage >= data.pages.length) {
        void fetchNextPage();
      }
    },
    [currentPage, data, hasUnfetchedPage, isFetchingNextPage, fetchNextPage],
  );

  const loaded = data?.pages.length;
  const hasNextPage =
    (loaded !== undefined && currentPage + 1 < loaded) || hasUnfetchedPage;

  return {
    data: data?.pages[currentPage]?.entries,
    error: q.error,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    hasNextPage,
  };
}
